/**
 * TenantLayout — portal shell for all /app/tenant/* routes.
 *
 * Topbar features:
 *  - Bell: fetches real notifications (pending document signatures +
 *    maintenance ticket status changes). Badge count. Click-outside closes.
 *  - Avatar: profile dropdown with name/email, Settings link, and Logout.
 */

import { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate, Navigate, Link } from 'react-router-dom';
import {
    Home, CreditCard, Wrench, FileText, Settings,
    LogOut, Menu, X, Bell, PenLine, CheckCircle2,
    AlertCircle, ChevronRight, MessageSquare,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../../context/useAppContext';
import { supabase } from '../../lib/supabase';
import { useHeartbeat } from '../../lib/presence';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import '../../dashboard.css';
import './TenantLayout.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Notification {
    id: string;
    type: string;
    title: string;
    body: string;
    href: string;
    read: boolean;
    created_at: string;
}

// ─── Nav items ────────────────────────────────────────────────────────────────

const NAV_DEFS = [
    { key: 'dashboard',   href: '/app/tenant/dashboard',   icon: Home,           end: true },
    { key: 'payments',    href: '/app/tenant/payments',    icon: CreditCard },
    { key: 'maintenance', href: '/app/tenant/maintenance', icon: Wrench },
    { key: 'documents',   href: '/app/tenant/documents',   icon: FileText },
    { key: 'chat',        href: '/app/tenant/chat',        icon: MessageSquare },
    { key: 'settings',    href: '/app/tenant/settings',    icon: Settings },
];

// ─── Notification icon helper ─────────────────────────────────────────────────

function NotifIcon({ type }: { type: string }) {
    if (type === 'document_sent') return (
        <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, background: 'rgba(245,158,11,0.12)', color: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PenLine size={15} />
        </div>
    );
    if (type === 'ticket_created' || type === 'ticket_status') return (
        <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, background: 'rgba(34,197,94,0.12)', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CheckCircle2 size={15} />
        </div>
    );
    if (type === 'new_message') return (
        <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, background: 'rgba(99,102,241,0.12)', color: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <MessageSquare size={15} />
        </div>
    );
    return (
        <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, background: 'rgba(239,68,68,0.12)', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <AlertCircle size={15} />
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function TenantLayout() {
    const [sidebarOpen, setSidebarOpen]   = useState(false);
    const [notifOpen, setNotifOpen]       = useState(false);
    const [profileOpen, setProfileOpen]   = useState(false);
    const [notifications, setNotifications] = useState<Notification[]>([]);

    const { user, authLoading, logout }   = useAppContext();
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Mark this tenant as online while the portal is mounted + tab is visible
    useHeartbeat();

    const notifRef   = useRef<HTMLDivElement>(null);
    const profileRef = useRef<HTMLDivElement>(null);

    // ── Close dropdowns on outside click ─────────────────────────────────────
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
            if (profileRef.current && !profileRef.current.contains(e.target as Node)) setProfileOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // ── Fetch notifications from DB ───────────────────────────────────────────
    const loadNotifs = async () => {
        if (!user.id) return;
        const { data } = await supabase
            .from('notifications')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(30);
        setNotifications((data ?? []) as Notification[]);
    };

    useEffect(() => {
        if (!user.id) return;
        loadNotifs();

        // Realtime: new notifications pushed immediately
        const channel = supabase
            .channel(`notifs:tenant:${user.id}`)
            .on(
                'postgres_changes',
                { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
                (payload) => {
                    setNotifications(prev => [payload.new as Notification, ...prev]);
                }
            )
            .subscribe();

        return () => { supabase.removeChannel(channel); };
    }, [user.id]);

    const unreadCount = notifications.filter(n => !n.read).length;

    const markAllRead = async () => {
        if (!user.id) return;
        const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
        if (!unreadIds.length) return;
        await supabase.from('notifications').update({ read: true }).in('id', unreadIds);
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    };

    const handleOpenNotif = () => {
        setNotifOpen(v => !v);
        setProfileOpen(false);
        if (!notifOpen) markAllRead();
    };

    const handleOpenProfile = () => {
        setProfileOpen(v => !v);
        setNotifOpen(false);
    };

    // In-app cursor glow
    useEffect(() => {
        const glow = document.getElementById('appCursorGlow');
        if (!glow) return;
        const onMove = (e: MouseEvent) => {
            glow.style.left = e.clientX + 'px';
            glow.style.top = e.clientY + 'px';
        };
        window.addEventListener('mousemove', onMove);
        return () => window.removeEventListener('mousemove', onMove);
    }, []);

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    // ── Role guard ────────────────────────────────────────────────────────────
    if (authLoading) {
        return (
            <div style={{
                display: 'flex', height: '100vh', alignItems: 'center',
                justifyContent: 'center', background: '#1e1f2e',
                color: '#9ca0b3', fontSize: '0.875rem',
            }}>
                {t('common.loading')}
            </div>
        );
    }
    if (!user.isAuthenticated) return <Navigate to="/login" replace />;
    if (user.role === 'landlord') return <Navigate to="/app/landlord/dashboard" replace />;

    const initials = (user.name || user.email || 'T').substring(0, 2).toUpperCase();

    return (
        <div className="dashboard">
            <div id="appCursorGlow" className="app-cursor-glow" />

            {/* ── Mobile Header ─────────────────────────────────────────── */}
            <div className="mobile-header">
                <button className="mobile-header__toggle" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
                    <Menu size={20} />
                </button>
                <span style={{ color: 'var(--color-text)', fontWeight: 700, marginLeft: '0.75rem', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <img src="/rentnova-mark.png" alt="" width={22} height={22} style={{ display: 'block', objectFit: 'contain', transform: 'translateY(-3px)' }} />
                    RentNova
                    <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '2px 7px', borderRadius: '4px', background: 'rgba(99,102,241,0.12)', color: '#6366f1', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        {t('nav.tenantBadge')}
                    </span>
                </span>
            </div>

            {/* ── Mobile Overlay ────────────────────────────────────────── */}
            {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

            {/* ── Sidebar ───────────────────────────────────────────────── */}
            <aside className={`sidebar${sidebarOpen ? ' sidebar--open' : ''}`}>
                <div className="sidebar__logo">
                    <img
                        src="/rentnova-mark.png"
                        alt="RentNova"
                        width={30}
                        height={30}
                        style={{ display: 'block', objectFit: 'contain', flexShrink: 0, transform: 'translateY(-4px)' }}
                    />
                    <span style={{ flex: 1 }}>RentNova</span>
                    <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', background: 'rgba(165,180,252,0.15)', color: '#a5b4fc', letterSpacing: '0.06em', textTransform: 'uppercase', flexShrink: 0 }}>
                        {t('nav.tenantBadge')}
                    </span>
                    {sidebarOpen && (
                        <button onClick={() => setSidebarOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca0b3', padding: '4px', marginLeft: '4px', display: 'flex' }} aria-label="Close menu">
                            <X size={16} />
                        </button>
                    )}
                </div>

                <div className="sidebar__section-label">{t('nav.myPortal')}</div>

                <nav className="sidebar__nav">
                    {NAV_DEFS.map((item) => (
                        <NavLink
                            key={item.href}
                            to={item.href}
                            end={'end' in item ? item.end : false}
                            className={({ isActive }) => `sidebar__link${isActive ? ' sidebar__link--active' : ''}`}
                            onClick={() => setSidebarOpen(false)}
                        >
                            <item.icon size={18} />
                            {t(`nav.${item.key}`)}
                        </NavLink>
                    ))}
                </nav>

                <div style={{ margin: '12px', marginTop: 'auto' }}>
                    <div className="sidebar__user" style={{ margin: 0, marginBottom: '4px' }}>
                        <div className="sidebar__user-avatar">{initials}</div>
                        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                            <div className="sidebar__user-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {user.name || user.email}
                            </div>
                            <div className="sidebar__user-email">{t('nav.myPortal')}</div>
                        </div>
                    </div>
                    <button
                        onClick={handleLogout}
                        style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            gap: '7px', width: '100%', padding: '8px 12px',
                            background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)',
                            borderRadius: '10px', cursor: 'pointer',
                            color: 'rgba(255,255,255,0.38)', fontSize: '0.75rem', fontWeight: 600,
                        }}
                        onMouseEnter={e => {
                            e.currentTarget.style.color = '#ef4444';
                            e.currentTarget.style.background = 'rgba(239,68,68,0.08)';
                            e.currentTarget.style.borderColor = 'rgba(239,68,68,0.18)';
                        }}
                        onMouseLeave={e => {
                            e.currentTarget.style.color = 'rgba(255,255,255,0.38)';
                            e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
                        }}
                    >
                        <LogOut size={13} /> {t('common.signOut')}
                    </button>
                </div>
            </aside>

            {/* ── Main Content ──────────────────────────────────────────── */}
            <div className="main-content">

                {/* Topbar */}
                <header className="topbar">
                    <div className="tenant-topbar-greeting">
                        {t('tenant.welcome', { name: user.name?.split(' ')[0] || t('nav.tenantBadge') })}
                    </div>

                    <div className="topbar__actions">

                        {/* ── Language switcher — discreet, top of app ── */}
                        <LanguageSwitcher variant="topbar" />

                        {/* ── Bell ──────────────────────────────────────── */}
                        <div ref={notifRef} style={{ position: 'relative' }}>
                            <button
                                className="topbar__icon-btn"
                                aria-label="Notifications"
                                onClick={handleOpenNotif}
                                style={{ position: 'relative' }}
                            >
                                <Bell size={18} />
                                {unreadCount > 0 && (
                                    <span style={{
                                        position: 'absolute', top: 2, right: 2,
                                        width: 8, height: 8, borderRadius: '50%',
                                        background: '#ef4444',
                                        border: '1.5px solid var(--color-sidebar-bg, #1e1f2e)',
                                    }} />
                                )}
                            </button>

                            {notifOpen && (
                                <div style={{
                                    position: 'absolute', top: 'calc(100% + 10px)', right: 0,
                                    width: 340, maxWidth: 'calc(100vw - 2rem)', background: '#fff', borderRadius: '16px',
                                    boxShadow: '0 20px 40px -8px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.06)',
                                    zIndex: 200, overflow: 'hidden',
                                }}>
                                    {/* Header */}
                                    <div style={{ padding: '1rem 1.25rem 0.75rem', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span style={{ fontWeight: 800, fontSize: '0.9375rem', color: '#1a1d2e' }}>{t('nav.notifications')}</span>
                                        {notifications.length > 0 && (
                                            <span style={{ fontSize: '0.75rem', color: '#6366f1', fontWeight: 600 }}>
                                                {notifications.length} item{notifications.length !== 1 ? 's' : ''}
                                            </span>
                                        )}
                                    </div>

                                    {/* Items */}
                                    <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                                        {notifications.length === 0 ? (
                                            <div style={{ padding: '2.5rem 1.25rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.875rem' }}>
                                                <Bell size={28} style={{ display: 'block', margin: '0 auto 0.75rem', opacity: 0.4 }} />
                                                {t('nav.noNotifications')}
                                            </div>
                                        ) : (
                                            notifications.map((n) => (
                                                <Link
                                                    key={n.id}
                                                    to={n.href}
                                                    onClick={() => setNotifOpen(false)}
                                                    style={{
                                                        display: 'flex', alignItems: 'flex-start', gap: '12px',
                                                        padding: '0.875rem 1.25rem', textDecoration: 'none',
                                                        borderBottom: '1px solid #f8fafc',
                                                        background: n.read ? 'transparent' : 'rgba(99,102,241,0.03)',
                                                        transition: 'background 0.15s',
                                                    }}
                                                    onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                                                    onMouseLeave={e => (e.currentTarget.style.background = n.read ? 'transparent' : 'rgba(99,102,241,0.03)')}
                                                >
                                                    <NotifIcon type={n.type} />
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontWeight: n.read ? 500 : 700, fontSize: '0.8125rem', color: '#1a1d2e', marginBottom: '2px' }}>{n.title}</div>
                                                        <div style={{ fontSize: '0.8125rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.body}</div>
                                                    </div>
                                                    <ChevronRight size={14} style={{ color: '#cbd5e1', flexShrink: 0, marginTop: 2 }} />
                                                </Link>
                                            ))
                                        )}
                                    </div>

                                    {/* Footer */}
                                    <div style={{ padding: '0.75rem 1.25rem', borderTop: '1px solid #f1f5f9', textAlign: 'center' }}>
                                        <Link
                                            to="/app/tenant/documents"
                                            onClick={() => setNotifOpen(false)}
                                            style={{ fontSize: '0.8125rem', color: '#6366f1', fontWeight: 600, textDecoration: 'none' }}
                                        >
                                            View all documents →
                                        </Link>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* ── Profile avatar ────────────────────────────── */}
                        <div ref={profileRef} style={{ position: 'relative' }}>
                            <button
                                onClick={handleOpenProfile}
                                aria-label="Profile menu"
                                style={{
                                    background: '#4f46e5', color: '#fff',
                                    width: 34, height: 34, borderRadius: '50%',
                                    border: '2px solid rgba(99,102,241,0.4)',
                                    cursor: 'pointer', fontSize: '0.7rem', fontWeight: 800,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    transition: 'border-color 0.2s, transform 0.15s',
                                    flexShrink: 0,
                                }}
                                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(99,102,241,0.8)'; (e.currentTarget as HTMLElement).style.transform = 'scale(1.05)'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(99,102,241,0.4)'; (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; }}
                            >
                                {initials}
                            </button>

                            {profileOpen && (
                                <div style={{
                                    position: 'absolute', top: 'calc(100% + 10px)', right: 0,
                                    width: 240, background: '#fff', borderRadius: '16px',
                                    boxShadow: '0 20px 40px -8px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.06)',
                                    zIndex: 200, overflow: 'hidden',
                                }}>
                                    {/* User info */}
                                    <div style={{ padding: '1.125rem 1.25rem', borderBottom: '1px solid #f1f5f9' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#4f46e5)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.875rem', flexShrink: 0 }}>
                                                {initials}
                                            </div>
                                            <div style={{ minWidth: 0 }}>
                                                <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: '#1a1d2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {user.name || 'Tenant'}
                                                </div>
                                                <div style={{ fontSize: '0.75rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {user.email}
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ marginTop: '0.75rem' }}>
                                            <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '3px 8px', borderRadius: '6px', background: 'rgba(99,102,241,0.1)', color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                {t('nav.tenantBadge')}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Menu items */}
                                    <div style={{ padding: '0.375rem' }}>
                                        <Link
                                            to="/app/tenant/settings"
                                            onClick={() => setProfileOpen(false)}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '10px',
                                                padding: '0.625rem 0.875rem', borderRadius: '10px',
                                                textDecoration: 'none', color: '#374151', fontWeight: 600, fontSize: '0.875rem',
                                                transition: 'background 0.15s',
                                            }}
                                            onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                                        >
                                            <Settings size={16} color="#94a3b8" />
                                            {t('common.settings')}
                                        </Link>
                                        <Link
                                            to="/app/tenant/documents"
                                            onClick={() => setProfileOpen(false)}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '10px',
                                                padding: '0.625rem 0.875rem', borderRadius: '10px',
                                                textDecoration: 'none', color: '#374151', fontWeight: 600, fontSize: '0.875rem',
                                                transition: 'background 0.15s',
                                            }}
                                            onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                                        >
                                            <FileText size={16} color="#94a3b8" />
                                            {t('nav.documents')}
                                        </Link>
                                    </div>

                                    {/* Logout */}
                                    <div style={{ padding: '0.375rem', borderTop: '1px solid #f1f5f9' }}>
                                        <button
                                            onClick={handleLogout}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
                                                padding: '0.625rem 0.875rem', borderRadius: '10px',
                                                background: 'none', border: 'none', cursor: 'pointer',
                                                color: '#ef4444', fontWeight: 600, fontSize: '0.875rem',
                                                transition: 'background 0.15s', textAlign: 'left',
                                            }}
                                            onMouseEnter={e => (e.currentTarget.style.background = '#fef2f2')}
                                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                                        >
                                            <LogOut size={16} />
                                            {t('common.signOut')}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                    </div>
                </header>

                {/* Page content */}
                <div className="page-wrapper tenant-page-bg">
                    <Outlet />
                </div>
            </div>
        </div>
    );
}
